'use client';
import React from 'react';
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, defs, linearGradient, stop } from 'recharts';

interface Props {
  period: '6m' | '1y' | '2y';
}

const DATA_1Y = [
  { month: 'Apr 25', submitted: 12, approved: 9, sentBack: 2 },
  { month: 'May 25', submitted: 18, approved: 14, sentBack: 1 },
  { month: 'Jun 25', submitted: 24, approved: 19, sentBack: 3 },
  { month: 'Jul 25', submitted: 8, approved: 11, sentBack: 0 },
  { month: 'Aug 25', submitted: 6, approved: 7, sentBack: 1 },
  { month: 'Sep 25', submitted: 21, approved: 15, sentBack: 2 },
  { month: 'Oct 25', submitted: 29, approved: 22, sentBack: 3 },
  { month: 'Nov 25', submitted: 35, approved: 28, sentBack: 4 },
  { month: 'Dec 25', submitted: 14, approved: 18, sentBack: 1 },
  { month: 'Jan 26', submitted: 19, approved: 13, sentBack: 2 },
  { month: 'Feb 26', submitted: 31, approved: 24, sentBack: 2 },
  { month: 'Mar 26', submitted: 27, approved: 20, sentBack: 1 },
];

const DATA_6M = DATA_1Y.slice(6);

const DATA_2Y = [
  { month: 'Apr 24', submitted: 9, approved: 7, sentBack: 1 },
  { month: 'Jun 24', submitted: 20, approved: 16, sentBack: 2 },
  { month: 'Aug 24', submitted: 5, approved: 8, sentBack: 0 },
  { month: 'Oct 24', submitted: 25, approved: 19, sentBack: 3 },
  { month: 'Dec 24', submitted: 11, approved: 15, sentBack: 1 },
  { month: 'Feb 25', submitted: 16, approved: 12, sentBack: 2 },
  ...DATA_1Y,
];

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string }) => {
  if (!active || !payload) return null;
  return (
    <div className="bg-white border border-[#e2e6f0] rounded-xl shadow-lg p-3 text-xs">
      <p className="font-700 text-[#0f1a35] mb-2">{label}</p>
      {payload.map((p) => (
        <div key={`tt-${p.name}`} className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-[#4a5578] capitalize">{p.name}</span>
          </div>
          <span className="font-700 text-[#0f1a35] tabular-nums">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function SubmissionsOverTimeChart({ period }: Props) {
  const data = period === '6m' ? DATA_6M : period === '2y' ? DATA_2Y : DATA_1Y;

  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
        <defs>
          <linearGradient id="gradSubmitted" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#1a2e5a" stopOpacity={0.15} />
            <stop offset="95%" stopColor="#1a2e5a" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="gradApproved" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#c9a84c" stopOpacity={0.12} />
            <stop offset="95%" stopColor="#c9a84c" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#e2e6f0" strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#8892a8' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: '#8892a8' }} axisLine={false} tickLine={false} />
        <Tooltip content={<CustomTooltip />} />
        <Area type="monotone" dataKey="submitted" stroke="#1a2e5a" strokeWidth={2} fill="url(#gradSubmitted)" name="submitted" />
        <Area type="monotone" dataKey="approved" stroke="#c9a84c" strokeWidth={2} fill="url(#gradApproved)" name="approved" />
        <Line type="monotone" dataKey="sentBack" stroke="#f87171" strokeWidth={1.5} dot={{ r: 3, fill: '#f87171' }} name="sentBack" strokeDasharray="4 2" />
      </ComposedChart>
    </ResponsiveContainer>
  );
}